import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { CHAT_AREA_WIDTH } from '../../constants';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { ConversationPanel } from '../conversation/conversation-panel';
import { MentionsPanel } from '../mentions/mentions-panel';
import { ThreadPanel } from '../threads/thread-panel';
import { ThreadsSectionPanel } from '../threads/threads-section-panel/threads-section-panel';

export const MainPanel = () => {
  const { t } = useTranslation('team-chat');
  const mainPanelView = useTeamChatSelector((ctx) => ctx.mainPanelView);
  const isNavExpanded = useTeamChatSelector((ctx) => ctx.isNavExpanded);
  const isSmallModeEnabled = useTeamChatSelector((ctx) => ctx.isSmallModeEnabled);

  const width = mainPanelView !== 'none' ? (isSmallModeEnabled ? '100%' : CHAT_AREA_WIDTH) : 0;

  return (
    <AnimatePresence>
      <motion.section
        css={[styles.container, isNavExpanded && styles.containerBorder, styles.listClasses]}
        initial={{ width }}
        animate={{ width }}
        exit={{ width: 0 }}
        transition={{ duration: 0.3, type: 'keyframes' }}
        layout
      >
        {mainPanelView === 'none' && <div css={{ width: CHAT_AREA_WIDTH }}>{t('Please select a conversation')}</div>}
        {(mainPanelView === 'conversation-static' || mainPanelView === 'conversation-dynamic') && <ConversationPanel />}
        {mainPanelView === 'mentions' && <MentionsPanel />}
        {mainPanelView === 'thread' && <ThreadPanel />}
        {mainPanelView === 'threads-section' && <ThreadsSectionPanel />}
      </motion.section>
    </AnimatePresence>
  );
};

MainPanel.displayName = 'MainPanel';

const styles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    width: CHAT_AREA_WIDTH,
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors.white,
  }),
  containerBorder: {
    borderLeft: `1px solid ${theme.colors.neutral10}`,
  },
  /* React virtuoso renders the list according to the view port. To add padding to the first message we had to access
     it with the help of index. Even if we did that the virtuoso component never incorporated the padding in the height
     calculation. And it added a scroller when even a single message was there. To fix the issue, this hack is added
     so that the padding is incorporated in the height calculation and it does not adds an un-necessary scroller. */
  listClasses: css`
    div[data-test-id='virtuoso-item-list'] {
      & > div:first-of-type {
        padding-top: ${theme.spacing(2.5)};
      }
    }
  `,
};
